import { useQuery, UseQueryResult } from "react-query";
import CustomerApiService from "../CustomerApiService";

/** One enabled interface entry, as returned by `GET /api/context` (spec §3.4). */
export interface EnabledInterface {
  id: string;
  url: string;
  title: string;
}

/** Shape of `GET /api/context` (spec §3.4). */
export interface CustomerContext {
  systemDomainId: string;
  customerName: string;
  enabledInterfaces: EnabledInterface[];
}

/**
 * Fetches `/api/context` once at app boot (React Query caches/dedupes it for every consumer —
 * every interface header AND its screen call this, see `useInterfaceAccess`, and share the one
 * cached result/request rather than each firing its own).
 *
 * A 401 here (no session yet, the normal case for an unauthenticated visitor) is handled locally:
 * `useInterfaceAccess` reads this hook's `isError` and renders placeholder chrome + an inline
 * notice instead of real content — a LATER 401 (a session that was confirmed, then died mid-use)
 * gets the identical treatment, via `SESSION_EXPIRED_EVENT` (see `useInterfaceAccess`'s own doc
 * comment); neither ever swaps the whole app tree.
 */
export function useCustomerContext(): UseQueryResult<CustomerContext, Error> {
  return useQuery<CustomerContext, Error>(
    "customer-context",
    () => CustomerApiService.fetch<CustomerContext>("GET", "/context"),
    {
      staleTime: 60_000,
      retry: false,
    }
  );
}
